// ── Ops Scorecard Config ─────────────────────────────────────────────
//
// Shared (server + client) definition of the FPM Manufacturing and FP
// Operations scorecards: per-metric rating bands (1–5), expected
// benchmarks, and formatting. Values mirror the approved FP/FPM
// Scorecard sheets (2026).
//
// Percent metrics are expressed as SUCCESS rates (0..1): e.g. the
// "Accuracy Error Rate" row on the sheet is the share of orders
// delivered per spec, so actual = 1 - errors/orders.

export type OpsMetricFormat = "percent" | "number" | "days";

export interface OpsMetricConfig {
  key: string;
  label: string;
  description: string;
  /** How the number is computed — shown in the metric's info tooltip. */
  formula: string;
  /** Where the underlying data comes from in HubSpot — shown in the info tooltip. */
  source: string;
  /** Thresholds for ratings 1..5, in band order from the scorecard sheet. */
  bands: [number, number, number, number, number];
  /** Expected benchmark (the "3 - Meets Expectations" target on the sheet). */
  benchmark: number;
  /** "higher" = value >= band threshold earns the rating; "lower" = value <= threshold. */
  polarity: "higher" | "lower";
  format: OpsMetricFormat;
  /** Metric not yet computable (blocked on upstream data). */
  pending?: string;
}

export const MANUFACTURING_METRICS: OpsMetricConfig[] = [
  {
    key: "capacity",
    label: "Capacity",
    description: "Maintain or improve capacity of 2.5 rings/day per jeweler",
    formula:
      "FP-manufactured orders completed in the period ÷ (Mon–Fri working days elapsed × jeweler count)",
    source:
      "HubSpot Production Pipeline deals where Manufacturer = “Faithful Platform”, dated by the Date Product Complete deal property. Jeweler count is set below.",
    bands: [2, 2.25, 2.5, 2.75, 3.0],
    benchmark: 2.5,
    polarity: "higher",
    format: "number",
  },
  {
    key: "otd",
    label: "OTD (On-Time Delivery)",
    description: "Deliver or exceed expected MFG date",
    formula:
      "Completed FP orders with Date Product Complete ≤ Manufacture Deadline ÷ completed FP orders that have a deadline",
    source:
      "HubSpot deal properties Date Product Complete and Manufacture Deadline on Production Pipeline deals where Manufacturer = “Faithful Platform”.",
    bands: [0.9, 0.92, 0.95, 0.97, 0.99],
    benchmark: 0.95,
    polarity: "higher",
    format: "percent",
  },
  {
    key: "repairRate",
    label: "Repair Rate",
    description: "Improve or exceed repair rate",
    formula:
      "FPM repair tickets opened in the period ÷ FP production orders from the trailing 36 months (as of period end)",
    source:
      "HubSpot Repair/Resize Service Pipeline tickets where Original Manufacturer = “FPM”, dated by ticket create date. Repair ticket history begins Aug 2024; tickets missing Original Manufacturer are not counted.",
    bands: [0.1, 0.08, 0.07, 0.05, 0.03],
    benchmark: 0.07,
    polarity: "lower",
    format: "percent",
  },
  {
    key: "fpy",
    label: "First Pass Yield",
    description: "Maintain or improve FPY",
    formula: "1 − (FP orders that entered “QCF - Back to Manufacturer” ÷ FP orders), by order date",
    source:
      "HubSpot stage history (date entered QCF) on Production Pipeline deals where Manufacturer = “Faithful Platform”. A deal that ever bounced back to the manufacturer counts against yield.",
    bands: [0.9, 0.92, 0.95, 0.97, 0.99],
    benchmark: 0.95,
    polarity: "higher",
    format: "percent",
  },
];

export const OPERATIONS_METRICS: OpsMetricConfig[] = [
  {
    key: "otd",
    label: "OTD (On-Time Delivery)",
    description: "Deliver or exceed expected MFG date",
    formula: "Orders marked Delivered On Time = “Yes” ÷ orders marked “Yes” or “No”",
    source:
      "The manually-set Delivered On Time deal property on Production Pipeline deals, by order date. Orders with the property blank are excluded.",
    bands: [0.9, 0.92, 0.95, 0.97, 0.99],
    benchmark: 0.95,
    polarity: "higher",
    format: "percent",
  },
  {
    key: "accuracyRate",
    label: "Accuracy Error Rate",
    description: "Product is delivered per expected specifications",
    formula: "1 − (orders with an accuracy error ÷ all production orders), by order date",
    source:
      "HubSpot deal property Type of Error = “Accuracy Error” (both QC Error and Consultant Error sub-types) on Production Pipeline deals.",
    bands: [0.9, 0.92, 0.97, 0.98, 0.99],
    benchmark: 0.97,
    polarity: "higher",
    format: "percent",
  },
  {
    key: "qualityRate",
    label: "Quality Error Rate",
    description: "Product is delivered without defect",
    formula: "1 − (orders with a quality error ÷ all production orders), by order date",
    source:
      "HubSpot deal property Type of Error = “Quality Error” on Production Pipeline deals. Reflects whatever the team records in HubSpot.",
    bands: [0.9, 0.92, 0.97, 0.98, 0.99],
    benchmark: 0.97,
    polarity: "higher",
    format: "percent",
  },
  {
    key: "repairResizeDays",
    label: "Repair/Resize # of Days to Complete",
    description: "Repair/Resizes turned around to client in required timeframe",
    formula:
      "Average of HubSpot's “Repair/Resize Days to Complete” (business days from the repair moving to Production to the shipment/pickup date), for Repair, Resize, and Repair and Resize tickets picked up in the period",
    source:
      "HubSpot Repair/Resize Service Pipeline tickets. Uses HubSpot's own calculated “Repair/Resize Days to Complete” field — business days (weekends excluded) from “Date repair/resize moved to Production” to the shipment/pickup date — averaged over tickets whose Request Type is Repair, Resize, or Repair and Resize. This mirrors HubSpot's Repair/Resize report exactly; tickets missing a production or pickup date, and non-repair types (Cleaning, Recast, ...), are excluded. History begins Aug 2024.",
    bands: [12, 10, 8, 7, 5],
    benchmark: 8,
    polarity: "lower",
    format: "days",
  },
];

export const RATING_LABELS = [
  "Unsatisfactory",
  "Needs Improvement",
  "Meets Expectations",
  "Above Expectations",
  "Exceeds Expectations",
] as const;

/**
 * Rate a value 1–5 against a metric's bands. Values that don't reach
 * band 1 still rate 1 (the sheet has no rating 0). Null → null.
 */
export function ratingFor(config: OpsMetricConfig, value: number | null): number | null {
  if (value === null) return null;
  let rating = 1;
  for (let i = 0; i < config.bands.length; i++) {
    const threshold = config.bands[i] as number;
    const meets = config.polarity === "higher" ? value >= threshold : value <= threshold;
    if (meets) rating = i + 1;
  }
  return rating;
}

export function formatOpsValue(format: OpsMetricFormat, value: number | null): string {
  if (value === null) return "—";
  switch (format) {
    case "percent":
      return `${(value * 100).toFixed(1)}%`;
    case "days":
      return `${value.toFixed(1)} days`;
    default:
      return value.toFixed(2);
  }
}
